// sessionReplay.js — m3-12 Session Drawer read-only replay shaping (pure, node-testable).
//
// Maps the ordered AG-UI event log (from GET /api/agent/session_replay/:id) into the drawer's
// timeline steps (icon + label + optional diff). READ-ONLY: this module produces nothing
// actionable — no submit body, no capability, no approval affordance (that's m3-07). It only
// describes what to render. The "Ask Vesta about this" handoff (m3-15) is separate.
//
// Step kinds (design docs/04): sub | tool | ok | edit | hold | warn. We derive the kind from
// the AG-UI event type, and surface a {m, p} diff for edit-shaped state deltas.

export const STEP_KINDS = Object.freeze(["sub", "tool", "ok", "edit", "hold", "warn"])

// S5: the render-directive names the drawer hoists out of the raw step timeline into a first-class
// `artifacts[]` lane (rendered as a ReportArtifact card atop the drawer body). A finished report
// (`agent.report`) and a money/go-live approval card (`agent.approval`) both carry their content in
// a render directive — left in the step list they collapse to a label-less "CUSTOM"/"tool" row.
export const ARTIFACT_RENDER_NAMES = Object.freeze(["agent.report", "agent.approval"])

// Pull the render DIRECTIVE off a CUSTOM event payload. Mirrors chatTimeline.js's proven extraction:
// the directive lives at `payload.value.render` (the report-spine contract), falling back to a flat
// `payload.render` if a flatter envelope ever lands. Returns the directive object or null.
function renderDirectiveFor(evt) {
  if (!evt || evt.type !== "CUSTOM") return null
  const p = evt.payload || {}
  const render = p.value && p.value.render != null ? p.value.render : p.render
  return render && typeof render === "object" ? render : null
}

// If a CUSTOM event carries an artifact render directive (agent.report | agent.approval), return the
// artifact view-model the drawer renders; otherwise null (the event stays a normal timeline step).
function artifactForEvent(evt) {
  const render = renderDirectiveFor(evt)
  if (render && ARTIFACT_RENDER_NAMES.includes(render.name)) {
    return { seq: evt.seq, name: render.name, render, at: evt.at }
  }
  return null
}

// Map an AG-UI event type → a drawer step kind. Unknown types render as a generic 'tool'
// timeline row (never crash, never an action).
export function stepKindForEvent(type) {
  switch (type) {
    case "TOOL_CALL_START":
    case "TOOL_CALL_ARGS":
    case "TOOL_CALL_END":
      return "tool"
    case "STATE_DELTA":
    case "STATE_SNAPSHOT":
      return "edit"
    case "RUN_FINISHED":
      return "ok"
    case "TASK_STATE_CHANGE":
      return "ok"
    case "RUN_ERROR":
      return "warn"
    case "STEP_BLOCKED":
      return "hold"
    case "SUBAGENT_HANDOFF":
      return "sub"
    case "TEXT_MESSAGE_CONTENT":
      return "tool" // narration row in the timeline
    default:
      return "tool"
  }
}

// Pull a human label from the event payload, falling back to the type.
function labelForEvent(evt) {
  const p = evt.payload || {}
  return p.label || p.title || p.tool_name || p.message || evt.type
}

// Extract a {m, p} (minus/plus) diff from a state-delta-shaped payload, or null.
function diffForEvent(evt) {
  const p = evt.payload || {}
  if (p.diff && (p.diff.m != null || p.diff.p != null)) return { m: p.diff.m, p: p.diff.p }
  if (p.before != null || p.after != null) return { m: p.before, p: p.after }
  return null
}

// Build the read-only drawer view model from the replay response.
export function buildReplay(response) {
  if (!response) return { session: null, steps: [], artifacts: [], isEmpty: true, readOnly: true }
  const events = response.events || []
  const artifacts = []
  const steps = []
  for (const evt of events) {
    // S5: hoist report/approval render directives into the artifacts lane (carry the FULL render so
    // ReportArtifact can render the report body OR the approval money-line). They do NOT also emit a
    // label-less timeline step. Dropping this collection step is the named RED (s5): the directive
    // falls through to a content-free "CUSTOM"/"tool" row and the drawer shows nothing renderable.
    const artifact = artifactForEvent(evt)
    if (artifact) {
      artifacts.push(artifact)
      continue
    }
    const kind = stepKindForEvent(evt.type)
    steps.push({
      seq: evt.seq,
      kind,
      label: labelForEvent(evt),
      diff: kind === "edit" ? diffForEvent(evt) : null,
      at: evt.at,
    })
  }
  return {
    session: response.session || null,
    // Item G: `steps` is still produced (isEmpty + tests depend on it) but the drawer no longer
    // renders the raw step log — the KILL was a client-render deletion in SessionDrawer only.
    steps,
    artifacts,
    isEmpty: steps.length === 0 && artifacts.length === 0,
    // The contract is read-only no matter what the server says; we surface it but never trust a
    // payload to grant an action. m3-07 is the ONLY path that adds an approval affordance.
    readOnly: true,
  }
}

// Hard guarantee for the gate: the drawer never produces a submit/capability/action from a
// replay. Any caller asking "can this drawer act?" gets false in m3-12.
export function canAct(_replayVm) {
  return false
}
